import React, { useEffect, useRef, useState } from "react";
import {
  Drawer,
  Box,
  Divider,
  IconButton,
  InputBase,
  Typography,
  Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import LogoIcon from "../../../../assets/Images/LogoLogin1.png";
import LogoText from "../../../../assets/Images/LogoLogin2.png";
import NavigationSections from "./NavigationSections";
import SidebarUserCard from "./SidebarUserCard";
import { useTranslation } from "react-i18next";
import { APP_VERSION } from "../../../../config/changelog";

const EXPANDED_WIDTH = 240;
const COLLAPSED_WIDTH = 72;

const SidebarComponent = ({ mobileOpen, handleDrawerToggle, WidthDraw }) => {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem("sidebar_collapsed") === "true";
    } catch {
      return false;
    }
  });

  // Buscador de módulos del sidebar. En modo colapsado el input no cabe: se
  // muestra una lupa que expande el sidebar y enfoca el campo.
  const [searchQuery, setSearchQuery] = useState("");
  const [pendingSearchFocus, setPendingSearchFocus] = useState(false);
  const searchInputRef = useRef(null);

  const theme = useTheme();
  const { t } = useTranslation();
  const sidebar = theme.palette.sidebar;
  const drawerWidth = collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH;

  useEffect(() => {
    WidthDraw(drawerWidth);
  }, [drawerWidth, WidthDraw]);

  useEffect(() => {
    try {
      localStorage.setItem("sidebar_collapsed", String(collapsed));
    } catch {
      // silently fail
    }
  }, [collapsed]);

  useEffect(() => {
    if (!collapsed && pendingSearchFocus) {
      // Esperar a que termine la transición de ancho del drawer (0.3s)
      const timer = setTimeout(() => searchInputRef.current?.focus(), 320);
      setPendingSearchFocus(false);
      return () => clearTimeout(timer);
    }
  }, [collapsed, pendingSearchFocus]);

  const toggleCollapse = () => setCollapsed((prev) => !prev);

  const expandAndFocusSearch = () => {
    setPendingSearchFocus(true);
    setCollapsed(false);
  };

  const drawerPaperSx = {
    boxSizing: "border-box",
    width: drawerWidth,
    transition: "width 0.3s ease",
    overflowX: "hidden",
    backgroundColor: sidebar.background,
    borderRight: "none",
  };

  const drawerContent = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      {/* Logo */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
          px: collapsed ? 1 : 2,
          py: 1.5,
          minHeight: 64,
        }}
      >
        {collapsed ? (
          <Box
            component="img"
            src={LogoIcon}
            alt="SantéPlus"
            onClick={toggleCollapse}
            sx={{
              maxWidth: 36,
              height: "auto",
              cursor: "pointer",
              filter: "brightness(0) invert(1)",
              opacity: 0.9,
            }}
          />
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.5,
                flex: 1,
              }}
            >
              <Box
                component="img"
                src={LogoIcon}
                alt="SantéPlus"
                sx={{
                  width: 36,
                  height: "auto",
                  filter: "brightness(0) invert(1)",
                  opacity: 0.9,
                }}
              />
              <Box
                component="img"
                src={LogoText}
                alt="SantéPlus"
                sx={{
                  maxWidth: 120,
                  height: "auto",
                  filter: "brightness(0) invert(1)",
                  opacity: 0.9,
                }}
              />
            </Box>
            <IconButton onClick={toggleCollapse} size="small" sx={{ color: sidebar.text }}>
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          </>
        )}
      </Box>

      <Divider sx={{ borderColor: sidebar.divider }} />

      {/* User Card */}
      <SidebarUserCard collapsed={collapsed} />

      <Divider sx={{ borderColor: sidebar.divider, mx: 1.5 }} />

      {/* Buscador de módulos */}
      <Box
        sx={{
          px: collapsed ? 0.5 : 1.5,
          pt: 1,
          pb: 0.5,
          display: "flex",
          justifyContent: "center",
        }}
      >
        {collapsed ? (
          <Tooltip
            title={t("nav.searchModules", "Search modules...")}
            placement="right"
            arrow
          >
            <IconButton
              onClick={expandAndFocusSearch}
              size="small"
              sx={{ color: sidebar.text }}
            >
              <SearchIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          <InputBase
            inputRef={searchInputRef}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") setSearchQuery("");
            }}
            placeholder={t("nav.searchModules", "Search modules...")}
            fullWidth
            startAdornment={
              <SearchIcon
                sx={{ fontSize: 18, mr: 1, color: sidebar.sectionLabel }}
              />
            }
            endAdornment={
              searchQuery ? (
                <IconButton
                  size="small"
                  onClick={() => setSearchQuery("")}
                  sx={{ color: sidebar.sectionLabel, p: 0.25 }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              ) : null
            }
            sx={{
              color: sidebar.text,
              fontSize: "0.8rem",
              backgroundColor: "rgba(255,255,255,0.06)",
              border: "1px solid",
              borderColor: sidebar.divider,
              borderRadius: 1,
              px: 1,
              py: 0.25,
              "&:focus-within": { borderColor: "#00bcd4" },
              "& input::placeholder": {
                color: sidebar.sectionLabel,
                opacity: 1,
              },
            }}
          />
        )}
      </Box>

      {/* Navigation Sections */}
      <NavigationSections
        collapsed={collapsed}
        searchQuery={collapsed ? "" : searchQuery}
      />

      {/* Bottom controls */}
      <Box sx={{ mt: "auto" }}>
        <Divider sx={{ borderColor: sidebar.divider }} />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: collapsed ? "center" : "space-between",
            px: collapsed ? 1 : 2,
            py: 1,
          }}
        >
          {collapsed ? (
            <Tooltip title="Expand" placement="right">
              <IconButton onClick={toggleCollapse} size="small" sx={{ color: sidebar.text }}>
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <>
              <Typography
                variant="caption"
                sx={{ color: sidebar.sectionLabel, fontSize: "0.7rem" }}
              >
                {t("nav.version", "Version")} {APP_VERSION}
              </Typography>
              <IconButton onClick={toggleCollapse} size="small" sx={{ color: sidebar.text }}>
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      aria-label="sidebar navigation"
    >
      {/* Drawer para moviles */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": drawerPaperSx,
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Drawer permanente para pantallas medianas o grandes */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", md: "block" },
          "& .MuiDrawer-paper": drawerPaperSx,
        }}
        open
      >
        {drawerContent}
      </Drawer>
    </Box>
  );
};

export default SidebarComponent;
